import React, { useState } from "react";
import { RoutingRule, Provider, Merchant } from "@/shared/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Route,
  MoreHorizontal,
  Pencil,
  Trash2,
  GripVertical,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Shuffle,
  GitBranch,
  Scale,
} from "lucide-react";

const routingModeConfig = {
  priority: { icon: GitBranch, label: "Priority", color: "bg-blue-100 text-blue-700" },
  round_robin: { icon: Shuffle, label: "Round Robin", color: "bg-purple-100 text-purple-700" },
  weighted: { icon: Scale, label: "Weighted", color: "bg-amber-100 text-amber-700" },
  least_load: { icon: Route, label: "Least Load", color: "bg-emerald-100 text-emerald-700" },
};

export default function Routing() {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t } = useLanguage();

  const queryClient = useQueryClient();

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["routing-rules"],
    queryFn: () => RoutingRule.list("priority"),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => Provider.list("priority"),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => Merchant.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => RoutingRule.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["routing-rules"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => RoutingRule.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["routing-rules"]);
      setFormOpen(false);
      setSelectedRule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => RoutingRule.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["routing-rules"]);
      setDeleteDialog(null);
    },
  });

  const handleSave = async (data) => {
    if (selectedRule) {
      await updateMutation.mutateAsync({ id: selectedRule.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setFormOpen(true);
  };

  const toggleRuleStatus = async (rule) => {
    const newStatus = rule.status === "active" ? "inactive" : "active";
    await updateMutation.mutateAsync({ id: rule.id, data: { ...rule, status: newStatus } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("routingRulesTitle")}</h1>
          <p className="text-slate-500">{t("configureProviderSelection")}</p>
        </div>
        <Button onClick={() => setFormOpen(true)} className="gap-2 w-full sm:w-auto hover:bg-blue-700 hover:scale-105 transition-all duration-300 shadow-sm hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700">
          <Plus className="h-4 w-4" />
          {t("addRule")}
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Route className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">{t("howRoutingWorks")}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t("routingDescription")}
            </p>
          </div>
        </div>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {rules.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 sm:p-12 text-center border border-slate-100">
            <Route className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">{t("noRoutingRules")}</h3>
            <p className="text-slate-500 mb-4">{t("createFirstRule")}</p>
            <Button onClick={() => setFormOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              {t("addRule")}
            </Button>
          </div>
        ) : (
          rules.map((rule, index) => {
            const mode = routingModeConfig[rule.routing_mode] || routingModeConfig.priority;
            const ModeIcon = mode.icon;
            const providerCodes = rule.provider_sequence ? JSON.parse(rule.provider_sequence) : [];

            return (
              <motion.div
                key={rule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className={`bg-white rounded-2xl p-4 sm:p-6 shadow-sm border transition-all ${rule.status === "active" ? "border-slate-100" : "border-slate-200 bg-slate-50 opacity-60"
                  }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <div className="hidden sm:block p-2 bg-slate-100 rounded-lg cursor-grab">
                      <GripVertical className="h-5 w-5 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 break-words">{rule.name === "Default Rule" ? t("defaultRule") : rule.name}</h3>
                        <Badge variant="outline" className="text-xs">{t("priority")} {rule.priority}</Badge>
                        <Badge className={`${rule.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"} border-0`}>
                          {rule.status === "active" ? (
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                          ) : (
                            <XCircle className="h-3 w-3 mr-1" />
                          )}
                          {rule.status}
                        </Badge>
                      </div>

                      {/* Conditions */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="outline" className="text-xs">
                          {t("type")}: {rule.transaction_type || t("allTypes").toLowerCase()}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {t("method")}: {rule.payment_method?.replace(/_/g, " ") || t("allMethods").toLowerCase()}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {t("amount")}: RS{rule.min_amount?.toLocaleString() || 0} - RS{rule.max_amount?.toLocaleString() || "∞"}
                        </Badge>
                        {rule.merchant_id && rule.merchant_id !== "all" && (
                          <Badge variant="outline" className="text-xs">
                            {t("merchant")}: {merchants.find(m => m.id === rule.merchant_id)?.code || rule.merchant_id}
                          </Badge>
                        )}
                      </div>

                      {/* Provider Sequence */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${mode.color} border-0 gap-1`}>
                          <ModeIcon className="h-3 w-3" />
                          {mode.label}
                        </Badge>
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          {providerCodes.map((code, i) => (
                            <React.Fragment key={code}>
                              {i > 0 && <ArrowRight className="h-4 w-4 text-slate-300" />}
                              <Badge variant="outline" className="font-mono break-all">
                                {code}
                              </Badge>
                            </React.Fragment>
                          ))}
                        </div>
                        {rule.failover_enabled && (
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">
                            Failover ({rule.max_failover_attempts}x)
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 self-end sm:self-auto">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(rule)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleRuleStatus(rule)}>
                        {rule.status === "active" ? (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            {t("disable")}
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            {t("enable")}
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteDialog(rule)} className="text-red-600">
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Rule Form Dialog */}
      <RuleFormDialog
        rule={selectedRule}
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedRule(null);
        }}
        onSave={handleSave}
        providers={providers}
        merchants={merchants}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteRoutingRule")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteConfirmation")} "{deleteDialog?.name === "Default Rule" ? t("defaultRule") : deleteDialog?.name}"? {t("affectTransactionRouting")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteDialog.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RuleFormDialog({ rule, open, onClose, onSave, providers, merchants }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState(
    rule || {
      name: "",
      status: "active",
      priority: 1,
      merchant_id: "all",
      transaction_type: "all",
      payment_method: "all",
      min_amount: 0,
      max_amount: 999999999,
      provider_sequence: "[]",
      routing_mode: "priority",
      failover_enabled: true,
      max_failover_attempts: 3,
      notes: "",
    }
  );
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (rule) {
      setFormData(rule);
    } else {
      setFormData({
        name: "",
        status: "active",
        priority: 1,
        merchant_id: "all",
        transaction_type: "all",
        payment_method: "all",
        min_amount: 0,
        max_amount: 999999999,
        provider_sequence: "[]",
        routing_mode: "priority",
        failover_enabled: true,
        max_failover_attempts: 3,
        notes: "",
      });
    }
  }, [rule, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const providerSequence = React.useMemo(() => {
    try {
      return JSON.parse(formData.provider_sequence || "[]");
    } catch {
      return [];
    }
  }, [formData.provider_sequence]);

  const toggleProvider = (code) => {
    const newSequence = providerSequence.includes(code)
      ? providerSequence.filter((c) => c !== code)
      : [...providerSequence, code];
    handleChange("provider_sequence", JSON.stringify(newSequence));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule ? t("editRoutingRule") : t("createRoutingRule")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("ruleName")} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder={t("defaultQrRoutePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("priority")}</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => handleChange("priority", parseInt(e.target.value))}
                min={1}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("transactionType")}</Label>
              <Select
                value={formData.transaction_type}
                onValueChange={(v) => handleChange("transaction_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTypes")}</SelectItem>
                  <SelectItem value="deposit">{t("depositsOnly")}</SelectItem>
                  <SelectItem value="withdrawal">{t("withdrawalsOnly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("paymentMethod")}</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(v) => handleChange("payment_method", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allMethods")}</SelectItem>
                  <SelectItem value="promptpay_qr">PromptPay QR</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="truemoney">TrueMoney</SelectItem>
                  <SelectItem value="linepay">LinePay</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("minAmount")}</Label>
              <Input
                type="number"
                value={formData.min_amount}
                onChange={(e) => handleChange("min_amount", parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("maxAmount")}</Label>
              <Input
                type="number"
                value={formData.max_amount}
                onChange={(e) => handleChange("max_amount", parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("specificMerchant")}</Label>
            <Select
              value={formData.merchant_id || "all"}
              onValueChange={(v) => handleChange("merchant_id", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("routingMode")}</Label>
            <Select
              value={formData.routing_mode}
              onValueChange={(v) => handleChange("routing_mode", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">{t("priorityMode")}</SelectItem>
                <SelectItem value="round_robin">{t("roundRobin")}</SelectItem>
                <SelectItem value="weighted">{t("weighted")}</SelectItem>
                <SelectItem value="least_load">{t("leastLoad")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("providerSequence")}</Label>
            <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-lg min-h-[60px]">
              {providers.map((p) => (
                <Badge
                  key={p.id}
                  variant={providerSequence.includes(p.code) ? "default" : "outline"}
                  className={`cursor-pointer transition-all ${providerSequence.includes(p.code)
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "hover:bg-slate-100"
                    }`}
                  onClick={() => toggleProvider(p.code)}
                >
                  {p.code}
                  {providerSequence.includes(p.code) && (
                    <span className="ml-1 text-xs opacity-70">
                      #{providerSequence.indexOf(p.code) + 1}
                    </span>
                  )}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              {t("clickProvidersOrder")}
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 p-4 bg-slate-50 rounded-lg">
            <div>
              <Label>{t("enableFailover")}</Label>
              <p className="text-sm text-slate-500">{t("autoTryNextProvider")}</p>
            </div>
            <Switch
              checked={formData.failover_enabled}
              onCheckedChange={(v) => handleChange("failover_enabled", v)}
            />
          </div>

          {formData.failover_enabled && (
            <div className="space-y-2">
              <Label>{t("maxFailoverAttempts")}</Label>
              <Input
                type="number"
                value={formData.max_failover_attempts}
                onChange={(e) => handleChange("max_failover_attempts", parseInt(e.target.value))}
                min={1}
                max={10}
              />
            </div>
          )}
        </div>

        <DialogFooter className="mt-6 flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !formData.name}>
            {saving ? t("saving") : rule ? t("update") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}